/**
 * Timezone utility functions for calendar timezone handling
 * Handles DST transitions properly using native JavaScript Intl API
 *
 * DST Handling Examples:
 * - London (Europe/London): GMT in winter, BST (GMT+1) in summer
 * - New York (America/New_York): EST in winter, EDT (EST+1) in summer
 * - Los Angeles (America/Los_Angeles): PST in winter, PDT (PST+1) in summer
 *
 * The native Intl API automatically handles:
 * - DST transition dates (e.g., last Sunday in March, first Sunday in November)
 * - Clock changes (spring forward, fall back)
 * - Historical DST changes
 * - Different DST rules for different countries
 */

import { logger } from "./logger";

export class TimezoneUtils {
  /**
   * Convert a date from one timezone to another
   * This properly handles DST transitions using the native Intl API
   */
  static convertDate(date: Date, fromTimezone: string, toTimezone: string): Date {
    try {
      // Validate date input explicitly so consumers can handle errors
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new RangeError("Invalid time value");
      }
      if (fromTimezone === toTimezone) {
        return new Date(date);
      }

      // Proper timezone conversion using Intl.DateTimeFormat
      // First, get the time in the source timezone
      const sourceFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: fromTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const targetFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: toTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      // Get the time components in both timezones
      const sourceParts = sourceFormatter.formatToParts(date);
      const targetParts = targetFormatter.formatToParts(date);

      // Create dates from the parts
      const sourceTime = new Date(
        parseInt(sourceParts.find((p) => p.type === "year")!.value),
        parseInt(sourceParts.find((p) => p.type === "month")!.value) - 1,
        parseInt(sourceParts.find((p) => p.type === "day")!.value),
        parseInt(sourceParts.find((p) => p.type === "hour")!.value),
        parseInt(sourceParts.find((p) => p.type === "minute")!.value),
        parseInt(sourceParts.find((p) => p.type === "second")!.value)
      );

      const targetTime = new Date(
        parseInt(targetParts.find((p) => p.type === "year")!.value),
        parseInt(targetParts.find((p) => p.type === "month")!.value) - 1,
        parseInt(targetParts.find((p) => p.type === "day")!.value),
        parseInt(targetParts.find((p) => p.type === "hour")!.value),
        parseInt(targetParts.find((p) => p.type === "minute")!.value),
        parseInt(targetParts.find((p) => p.type === "second")!.value)
      );

      // Calculate the offset and apply it to the original date
      const offset = targetTime.getTime() - sourceTime.getTime();
      const convertedDate = new Date(date.getTime() + offset);

      return convertedDate;
    } catch (error) {
      logger.warn("Error converting timezone", { error });
      throw error;
    }
  }

  /**
   * Get timezone offset in minutes for a specific date
   * This properly handles DST transitions
   */
  static getTimezoneOffset(timezone: string, date: Date = new Date()): number {
    try {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new RangeError("Invalid time value");
      }

      // Derive wall-clock components in the target timezone for the given instant
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const parts = formatter.formatToParts(date);
      const year = parseInt(parts.find((p) => p.type === "year")!.value, 10);
      const month = parseInt(parts.find((p) => p.type === "month")!.value, 10);
      const day = parseInt(parts.find((p) => p.type === "day")!.value, 10);
      const hour = parseInt(parts.find((p) => p.type === "hour")!.value, 10);
      const minute = parseInt(parts.find((p) => p.type === "minute")!.value, 10);
      const second = parseInt(parts.find((p) => p.type === "second")!.value, 10);

      // Treat the timezone-adjusted wall clock as UTC to compute offset from the actual UTC instant
      const wallClockAsUTC = Date.UTC(year, month - 1, day, hour, minute, second);
      const offsetMinutes = Math.round((wallClockAsUTC - date.getTime()) / 60000);
      return offsetMinutes; // e.g., New York: -300 (Jan), -240 (Jul)
    } catch (error) {
      logger.warn("Error getting timezone offset", { error });
      return 0;
    }
  }

  /**
   * Check if a timezone is currently in DST
   */
  static isDST(timezone: string, date: Date = new Date()): boolean {
    try {
      // Get the offset for January 1st (standard time)
      const janOffset = this.getTimezoneOffset(timezone, new Date(date.getFullYear(), 0, 1));

      // If the offsets are different, the timezone observes DST
      // If current offset matches summer offset, we're in DST
      const currentOffset = this.getTimezoneOffset(timezone, date);
      return currentOffset !== janOffset;
    } catch (error) {
      console.warn("Error checking DST:", error);
      return false;
    }
  }

  /**
   * Get the next DST transition date for a timezone
   */
  static getNextDSTTransition(timezone: string, fromDate: Date = new Date()): Date | null {
    try {
      const isCurrentlyDST = this.isDST(timezone, fromDate);

      // Check each day for the next 365 days to find the next transition
      for (let i = 1; i <= 365; i++) {
        const checkDate = new Date(fromDate.getTime() + i * 24 * 60 * 60 * 1000);
        const checkIsDST = this.isDST(timezone, checkDate);

        if (checkIsDST !== isCurrentlyDST) {
          return checkDate;
        }
      }

      return null;
    } catch (error) {
      console.warn("Error getting next DST transition:", error);
      return null;
    }
  }

  /**
   * Format date for display in specific timezone
   */
  static formatDateInTimezone(date: Date, timezone: string, options?: Intl.DateTimeFormatOptions): string {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        ...options,
      }).format(date);
    } catch (error) {
      console.warn("Error formatting date in timezone:", error);
      return date.toLocaleString();
    }
  }

  /**
   * Get current time in specific timezone
   */
  static getCurrentTimeInTimezone(timezone: string): Date {
    try {
      const now = new Date();
      // Get the current time in the target timezone using the same approach as convertDate
      const targetFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const targetParts = targetFormatter.formatToParts(now);
      const targetTime = new Date(
        parseInt(targetParts.find((p) => p.type === "year")!.value),
        parseInt(targetParts.find((p) => p.type === "month")!.value) - 1,
        parseInt(targetParts.find((p) => p.type === "day")!.value),
        parseInt(targetParts.find((p) => p.type === "hour")!.value),
        parseInt(targetParts.find((p) => p.type === "minute")!.value),
        parseInt(targetParts.find((p) => p.type === "second")!.value)
      );

      return targetTime;
    } catch (error) {
      console.warn("Error getting current time in timezone:", error);
      return new Date();
    }
  }

  /**
   * Convert event times to user's timezone for display
   */
  static convertEventToUserTimezone(event: any, userTimezone: string): any {
    if (!event.startTime || !event.endTime) return event;

    try {
      // Convert start and end times to user timezone
      const startTime = new Date(event.startTime);
      const endTime = new Date(event.endTime);

      // Create new event with converted times
      return {
        ...event,
        startTime: this.convertDate(startTime, "UTC", userTimezone),
        endTime: this.convertDate(endTime, "UTC", userTimezone),
      };
    } catch (error) {
      console.warn("Error converting event to user timezone:", error);
      return event;
    }
  }

  /**
   * Convert working hours from user's timezone to UTC for storage
   * This properly handles DST transitions
   */
  static convertWorkingHoursToUTC(workingHours: any, userTimezone: string): any {
    if (!workingHours || !userTimezone) return workingHours;

    try {
      const convertedHours: any = {};

      for (const [day, hours] of Object.entries(workingHours)) {
        if (hours && typeof hours === "object" && "start" in hours && "end" in hours) {
          const dayHours = hours as { start: string; end: string; enabled: boolean };

          if (dayHours.enabled && dayHours.start && dayHours.end) {
            // Create dates for today with the working hours
            const today = new Date();
            const startDate = new Date(`${today.toISOString().split("T")[0]}T${dayHours.start}:00`);
            const endDate = new Date(`${today.toISOString().split("T")[0]}T${dayHours.end}:00`);

            // Convert from user timezone to UTC
            const startUTC = this.convertDate(startDate, userTimezone, "UTC");
            const endUTC = this.convertDate(endDate, userTimezone, "UTC");

            // Convert back to time strings in UTC
            const startUTCString = startUTC.toTimeString().split(" ")[0].substring(0, 5);
            const endUTCString = endUTC.toTimeString().split(" ")[0].substring(0, 5);

            convertedHours[day] = {
              ...dayHours,
              start: startUTCString,
              end: endUTCString,
            };
          } else {
            convertedHours[day] = dayHours;
          }
        } else {
          convertedHours[day] = hours;
        }
      }

      return convertedHours;
    } catch (error) {
      console.warn("Error converting working hours to UTC:", error);
      return workingHours;
    }
  }

  /**
   * Convert working hours from UTC to user's timezone for display
   * This properly handles DST transitions
   */
  static convertWorkingHoursFromUTC(workingHours: any, userTimezone: string): any {
    if (!workingHours || !userTimezone) return workingHours;

    try {
      const convertedHours: any = {};

      for (const [day, hours] of Object.entries(workingHours)) {
        if (hours && typeof hours === "object" && "start" in hours && "end" in hours) {
          const dayHours = hours as { start: string; end: string; enabled: boolean };

          if (dayHours.enabled && dayHours.start && dayHours.end) {
            // Create dates for today with the UTC working hours
            const today = new Date();
            const startDate = new Date(`${today.toISOString().split("T")[0]}T${dayHours.start}:00`);
            const endDate = new Date(`${today.toISOString().split("T")[0]}T${dayHours.end}:00`);

            // Convert from UTC to user timezone
            const startUser = this.convertDate(startDate, "UTC", userTimezone);
            const endUser = this.convertDate(endDate, "UTC", userTimezone);

            // Convert back to time strings in user timezone
            const startUserString = startUser.toTimeString().split(" ")[0].substring(0, 5);
            const endUserString = endUser.toTimeString().split(" ")[0].substring(0, 5);

            convertedHours[day] = {
              ...dayHours,
              start: startUserString,
              end: endUserString,
            };
          } else {
            convertedHours[day] = dayHours;
          }
        } else {
          convertedHours[day] = hours;
        }
      }

      return convertedHours;
    } catch (error) {
      console.warn("Error converting working hours from UTC:", error);
      return workingHours;
    }
  }

  /**
   * Convert event times from user's timezone to UTC for storage
   */
  static convertEventFromUserTimezone(event: any, userTimezone: string): any {
    if (!event.startTime || !event.endTime) return event;

    try {
      // Convert start and end times from user timezone to UTC
      const startTime = new Date(event.startTime);
      const endTime = new Date(event.endTime);

      // Create new event with converted times
      return {
        ...event,
        startTime: this.convertDate(startTime, userTimezone, "UTC"),
        endTime: this.convertDate(endTime, userTimezone, "UTC"),
      };
    } catch (error) {
      console.warn("Error converting event from user timezone:", error);
      return event;
    }
  }

  /**
   * Check if a timezone is valid
   */
  static isValidTimezone(timezone: string): boolean {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get timezone display name
   */
  static getTimezoneDisplayName(timezone: string): string {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "long",
      });
      return formatter.formatToParts(new Date()).find((part) => part.type === "timeZoneName")?.value || timezone;
    } catch (error) {
      return timezone;
    }
  }

  /**
   * Get timezone abbreviation (e.g., "PST", "EST")
   */
  static getTimezoneAbbreviation(timezone: string): string {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "short",
      });
      return formatter.formatToParts(new Date()).find((part) => part.type === "timeZoneName")?.value || "";
    } catch (error) {
      return "";
    }
  }

  /**
   * Convert event times to UTC for efficient time window searching
   */
  static convertToUTC(startTime: Date, endTime: Date, timezone = "UTC"): { startTimeUTC: Date; endTimeUTC: Date } {
    const startTimeUTC = this.convertDate(startTime, timezone, "UTC");
    const endTimeUTC = this.convertDate(endTime, timezone, "UTC");
    return { startTimeUTC, endTimeUTC };
  }

  /**
   * Convert a Date object to ISO string format without UTC conversion
   * Preserves the original time as entered (doesn't convert from browser timezone to UTC)
   * Returns format: "YYYY-MM-DDTHH:mm:ss"
   */
  static convertToString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  /**
   * Get the start and end of this week (Monday to Sunday) in a specific timezone
   * Returns UTC dates representing the start of Monday (00:00:00) and end of Sunday (23:59:59.999) in the target timezone
   */
  static getWeekDateRange(timezone: string): { start: Date; end: Date } {
    const now = new Date();

    // Get current date in the target timezone (YYYY-MM-DD format)
    const dateFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const dateStr = dateFormatter.format(now);

    // Create a date at noon on today in UTC (to avoid DST edge cases)
    const noonUTC = new Date(dateStr + "T12:00:00Z");

    // Get the day of week in the target timezone using Intl API
    const dayOfWeekFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
    });
    const weekdayStr = dayOfWeekFormatter.format(noonUTC);

    // Map weekday string to number (0 = Sunday, 1 = Monday, etc.)
    const weekdayMap: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    const dayOfWeek = weekdayMap[weekdayStr] ?? 1; // Default to Monday if unknown

    // Calculate days to subtract to get to Monday (Monday = 1)
    // If today is Sunday (0), we go back 6 days to get to Monday
    // If today is Monday (1), we go back 0 days
    // Formula: ((dayOfWeek + 6) % 7) gives us days to subtract
    const daysToMonday = (dayOfWeek + 6) % 7;

    // Calculate Monday at noon UTC
    const mondayNoonUTC = new Date(noonUTC.getTime() - daysToMonday * 24 * 60 * 60 * 1000);

    // Get offset for Monday (might be different due to DST)
    const mondayOffsetMinutes = this.getTimezoneOffset(timezone, mondayNoonUTC);

    // Start of week: Monday 00:00:00 in target timezone
    const startOfWeekUTC = new Date(mondayNoonUTC.getTime() - 12 * 60 * 60 * 1000 - mondayOffsetMinutes * 60 * 1000);

    // End of week: Sunday 23:59:59.999 in target timezone (6 days after Monday start)
    const sundayNoonUTC = new Date(mondayNoonUTC.getTime() + 6 * 24 * 60 * 60 * 1000);
    const sundayOffsetMinutes = this.getTimezoneOffset(timezone, sundayNoonUTC);
    const endOfWeekUTC = new Date(sundayNoonUTC.getTime() + 12 * 60 * 60 * 1000 - sundayOffsetMinutes * 60 * 1000 - 1);

    return { start: startOfWeekUTC, end: endOfWeekUTC };
  }

  /**
   * Get the start and end of today in a specific timezone
   * Returns UTC dates representing the start (00:00:00) and end (23:59:59.999) of today in the target timezone
   */
  static getTodayDateRange(timezone: string): { start: Date; end: Date } {
    const now = new Date();

    // Get current date in the target timezone (YYYY-MM-DD format)
    const dateFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const dateStr = dateFormatter.format(now);

    // Use noon UTC on the target date as a reference point to avoid DST edge cases
    const noonUTC = new Date(dateStr + "T12:00:00Z");

    // Get the timezone offset at noon (handles DST correctly)
    // Offset is in minutes: positive means ahead of UTC, negative means behind UTC
    const offsetMinutes = this.getTimezoneOffset(timezone, noonUTC);

    // Calculate start of day (midnight) in target timezone as UTC
    // Example: For EST (UTC-5), if noon UTC = 7 AM EST, then midnight EST = 5 AM UTC
    // Formula: noon UTC - 12 hours - offset = 12:00 - 12:00 - (-5:00) = 05:00 UTC
    const startOfDayUTC = new Date(noonUTC.getTime() - 12 * 60 * 60 * 1000 - offsetMinutes * 60 * 1000);

    // Calculate end of day (23:59:59.999) in target timezone as UTC
    // Just before the start of the next day
    const endOfDayUTC = new Date(noonUTC.getTime() + 12 * 60 * 60 * 1000 - offsetMinutes * 60 * 1000 - 1);

    return { start: startOfDayUTC, end: endOfDayUTC };
  }
}
